// Работен лист „Нова доставка / Редакция" (routes /inventory/delivery/new и
// /inventory/delivery/:id/edit, вътре в Inventory layout-а). Оркестраторът:
// състоянието на документа, черновата (локално + на сървъра, със заключване —
// `useDeliveryDraft` над общото `components/drafts/useWorksheetDraft`),
// зареждането (редакция / prefill / фактура над стокови / чернова), клавишите
// и записа през модала „Запис на доставката".
//
// Части в ./components: Header (полетата), Search (търсачката), Table + Cards
// (редовете) с `useDeliveryRowsActions`, Footer, SaveModal, ProblemModals,
// Notes (стоковите към фактура). Клавиатурата е общата за модула
// (gridNavigation / useWorksheetSearch / useWorksheetKeyboard); Ctrl/Cmd+Enter
// отваря модала за запис.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { App, type RefSelectProps } from "antd";
import dayjs from "dayjs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../hooks/useViewport";
import {
  usePackagings,
  useProducts,
  useSuppliers,
  useWarehouses,
  useLastPrices,
  useStockBalances,
  useSupplierDeliveries,
  useInventorySettings,
} from "../../../hooks/useInventory";
import type { Delivery, DeliveryDocumentType } from "../../../api/inventory";
import { clampDecimals } from "../components/qty";
import { isDeletedProduct, pickableProducts } from "../components/product/deletedProducts";
import { useGridNavigation } from "../components/gridNavigation";
import { useWorksheetKeyboard } from "../components/useWorksheetKeyboard";
import { useWorksheetSearch } from "../components/useWorksheetSearch";
import { InventoryTheme } from "../components/InventoryTheme";
import { DraftPresence } from "../components/drafts/DraftWorksheetParts";
import { DeliveryWorksheetHeader } from "./components/DeliveryWorksheetHeader";
import type { DeliverySearchRow } from "./components/DeliveryWorksheetSearch";
import { DeliverySearchBar } from "./components/DeliverySearchBar";
import { DeliveryProductsCard } from "./components/DeliveryProductsCard";
import { draftSummaryOf } from "./components/deliveryWorksheetHelpers";
import { useDeliveryCatalogItems } from "./components/useDeliveryCatalogItems";
import { useDeliveryRowsActions } from "./components/useDeliveryRowsActions";
import { DeliveryWorksheetFooter } from "./components/DeliveryWorksheetFooter";
import { DeliveryNotesLinkedBanner, DeliveryNotesOfferBanner } from "./components/DeliveryNotes";
import { useInvoiceNotes } from "./components/useInvoiceNotes";
import { useDeliveryTotalAdjust } from "./components/useDeliveryTotalAdjust";
import { DeliveryWorksheetModals } from "./components/DeliveryWorksheetModals";
import { useDeliverySave } from "./components/useDeliverySave";
import { useDeliveryLeave } from "./components/useDeliveryLeave";
import { DeliveryTypeBadge } from "./components/DeliveryBadges";
import { useDeliveryLoad, type DeliveryRouterState } from "./components/useDeliveryLoad";
import { useDeliveryDraft } from "./components/useDeliveryDraft";
import { docTypeOf } from "./components/deliveryMeta";
import {
  COL_COUNT,
  PICK_COLS,
  groupDeliveryLines,
  orderOfLines,
  type DeliveryDraft,
} from "./components/deliveryWorksheetShared";

type Props = {
  warehouseId?: number | null;
  mode: "new" | "edit";
  // Регистрира пазач за незапазени промени в layout-а (Inventory.tsx), за да
  // могат менюто/стрелката да питат преди напускане. null при демонтаж.
  onRegisterLeaveGuard?: (guard: ((proceed: () => void) => void) | null) => void;
};

const LIST_PATH = "/inventory/delivery";

export const DeliveryWorksheetPage = ({ warehouseId = null, mode, onRegisterLeaveGuard }: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const { deliveryId } = useParams();
  const { isDesktop, isNarrow } = useViewport();
  const routerState = (location.state as DeliveryRouterState) ?? null;

  const { data: suppliers = [] } = useSuppliers();
  const { data: warehouses = [] } = useWarehouses();
  const productsQuery = useProducts();
  const packagingsQuery = usePackagings();
  const products = useMemo(() => productsQuery.data ?? [], [productsQuery.data]);
  const packagings = useMemo(() => packagingsQuery.data ?? [], [packagingsQuery.data]);
  const { data: settings } = useInventorySettings();
  const vatRegistered = settings?.vatRegistered ?? true;

  // ─── Документът ──────────────────────────────────────────────────────────
  const [date, setDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [documentType, setDocumentType] = useState<DeliveryDocumentType>("invoice");
  const [supplierId, setSupplierId] = useState<number | null>(routerState?.invoiceForNotes?.supplierId ?? null);
  const [whId, setWhId] = useState<number | null>(warehouseId);
  const [note, setNote] = useState("");
  const [templateId, setTemplateId] = useState<number | null>(null);
  // При редакция: стоковата, която вече е във фактура, не сменя типа си
  const [editedDelivery, setEditedDelivery] = useState<Delivery | null>(null);

  useEffect(() => {
    if (whId == null && warehouseId != null) setWhId(warehouseId);
  }, [warehouseId, whId]);

  // ─── Справочници ──────────────────────────────────────────────────────────
  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const decimalsOf = useCallback(
    (productId: number): number => clampDecimals(productById.get(productId)?.decimalPlaces),
    [productById],
  );
  const packagingById = useMemo(() => new Map(packagings.map((p) => [p.id, p])), [packagings]);
  const { data: stockBalances = [] } = useStockBalances(whId);
  const balanceByProduct = useMemo(
    () => new Map(stockBalances.map((b) => [b.productId, b])),
    [stockBalances],
  );
  const lastPricesQuery = useLastPrices(whId, supplierId);
  const lastPriceByProduct = useMemo(
    () => new Map((lastPricesQuery.data ?? []).map((lp) => [lp.productId, lp])),
    [lastPricesQuery.data],
  );
  const supplierDeliveriesQuery = useSupplierDeliveries(mode === "edit" ? null : supplierId);
  // фактура само над стокови няма свои редове — като темплейт е празна
  const supplierDeliveries = (supplierDeliveriesQuery.data ?? []).filter((delivery) => delivery.lines.length > 0);

  // ДДС: само при регистрирана фирма И доставчик с ДДС номер
  const supplier = supplierId == null ? null : (suppliers.find((s) => s.id === supplierId) ?? null);
  const supplierName = supplier?.name ?? null;
  const vatApplies = vatRegistered && !!supplier?.vatNumber?.trim();

  // Фактура над стокови: стоковите на доставчика, избраните и сумите им
  const {
    notesOfSupplier,
    pickerNotes,
    linkedNoteIds,
    setLinkedNoteIds,
    setLinkedNotesExtra,
    linkedNotes,
    hasLinkedNotes,
    invoiceNotes,
    notesSum,
  } = useInvoiceNotes({
    documentType,
    supplierId,
    initialNoteIds: routerState?.invoiceForNotes?.noteIds ?? [],
  });

  // ─── „Незапазени промени" + черновата (localStorage + сървър) ────────────
  // Състоянието и зареждането се четат лениво — редовете идват от hook-а по-долу.
  const draft = useDeliveryDraft({
    mode,
    homePath: LIST_PATH,
    routerState,
    sheet: () => ({
      date,
      invoiceNumber,
      supplierId,
      supplierName,
      whId,
      note,
      rows,
      order: addedIds,
      documentType,
      totalGross,
    }),
    describeLines: (payload) => draftSummaryOf(payload, productById, packagingById, decimalsOf),
    apply: (payload) => applyDraft(payload),
    onLoadError: () => onLoadError(),
  });
  const { dirtyRef, touch, resume } = draft;

  // ─── Търсачката и решетката ─────────────────────────────────────────────
  // Клетките са `dwx-{продукт}-{колона}`; колона 1 = „Количество".
  const searchBox = useWorksheetSearch({
    getResultCount: () => searchResults.length,
    onPickIndex: (index) => pickFromSearch(searchResults[index].id),
    focusFirst: () => focusFirst(1),
  });
  const rowKeysRef = useRef<number[]>([]);
  const { focusCell, focusFirst, focusSideways, onGridKey, isInGrid, cellId } = useGridNavigation({
    prefix: "dwx",
    colCount: COL_COUNT,
    getRowKeys: () => rowKeysRef.current,
    onEscape: searchBox.focusSearch,
    onLeaveTop: searchBox.focusSearch,
    enterOpensCols: [...PICK_COLS],
  });

  const [editLoaded, setEditLoaded] = useState(false);
  const {
    rows,
    addedIds,
    addedProducts,
    addedSet,
    rowOf,
    calcRow,
    qtyBase,
    patchRow,
    removeRow,
    replaceAllRows,
    addProduct,
    addFromCatalog,
    onCellChange,
    onCellBlur,
    setPricedSupplier,
    validEntries,
    computeIssues,
    totalNet,
    totalGross,
    unitTotals,
    planAdjustTotal,
    applyAdjustTotal,
  } = useDeliveryRowsActions({
    mode,
    warehouseId: whId,
    hasWarehouses: warehouses.length > 0,
    supplierId,
    vatApplies,
    productById,
    packagingById,
    lastPriceByProduct,
    lastPricesReady: lastPricesQuery.isSuccess,
    decimalsOf,
    recalcNeeded: editLoaded,
    referencesReady: productsQuery.isSuccess && packagingsQuery.isSuccess,
    onTouch: touch,
    focusCell,
  });
  rowKeysRef.current = addedIds;

  // ─── Напускане: нова — тихо (черновата се записва веднага); редакция — пита
  const requestLeave = useDeliveryLeave({
    mode,
    dirtyRef,
    flushDraft: draft.sync.flush,
    onRegisterLeaveGuard,
    describeRows: draft.describeRows,
  });
  const leaveToList = () => requestLeave(() => navigate(LIST_PATH));

  // ─── Зареждане: редакция / prefill / фактура над стокови / чернова ───────
  const onLoadError = () => {
    message.error(t("inventoryPage.errLoad"));
    navigate(LIST_PATH);
  };
  function applyDraft(payload: DeliveryDraft) {
    setDate(payload.date || dayjs().format("YYYY-MM-DD"));
    setInvoiceNumber(payload.invoiceNumber ?? "");
    setSupplierId(payload.supplierId ?? null);
    setPricedSupplier(payload.supplierId ?? null);
    setWhId(payload.whId ?? warehouseId);
    setNote(payload.note ?? "");
    setDocumentType(payload.documentType ?? "invoice");
    replaceAllRows(payload.rows, payload.order);
    setEditLoaded(true); // преизчисляване при заредени справочници
  }
  useDeliveryLoad({
    mode,
    deliveryId,
    routerState,
    warehouseId,
    decimalsOf,
    actions: {
      setHeader: (header) => {
        setDate(header.date);
        setInvoiceNumber(header.invoiceNumber);
        setSupplierId(header.supplierId);
        setPricedSupplier(header.supplierId);
        setWhId(header.warehouseId);
        setNote(header.note);
        setDocumentType(header.documentType);
      },
      setDate,
      setDocumentType,
      replaceAllRows,
      setEditLoaded,
      setEditedDelivery,
      setLinkedNoteIds,
      setLinkedNotesExtra,
      onLoadError,
    },
  });

  // ─── Търсачката ─────────────────────────────────────────────────────────
  const { searchWords } = searchBox;
  const searchResults = useMemo(() => {
    if (!searchWords.length) return [];
    return pickableProducts(products)
      .filter((p) => searchWords.every((word) => p.name.toLowerCase().includes(word)))
      .sort((a, b) => a.name.localeCompare(b.name, "bg"))
      .slice(0, 40);
  }, [products, searchWords]);
  const searchRows = useMemo<DeliverySearchRow[]>(
    () =>
      searchResults.map((product) => ({
        product,
        added: addedSet.has(product.id),
        available: balanceByProduct.get(product.id)?.quantity ?? 0,
        lastPrice: lastPriceByProduct.get(product.id) ?? null,
        decimals: decimalsOf(product.id),
      })),
    [searchResults, addedSet, balanceByProduct, lastPriceByProduct, decimalsOf],
  );
  const pickFromSearch = (productId: number) => {
    searchBox.setSearch("");
    if (addedSet.has(productId)) {
      const name = productById.get(productId)?.name ?? "";
      message.info(t("inventoryPage.alreadyInDelivery", { name }));
    }
    addProduct(productId);
  };

  // ─── Модалите и записът ──────────────────────────────────────────────────
  const [productModal, setProductModal] = useState<{ initialName: string } | null>(null);
  // новият продукт — фокусът отива в „Количество" СЛЕД затварянето на модала
  const createdProductRef = useRef<number | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [problemFor, setProblemFor] = useState<number | null>(null);
  // Доставчикът е задължителен за нова доставка — при запис без него фокусът отива там
  const supplierSelectRef = useRef<RefSelectProps>(null);
  const nameOf = (productId: number) => productById.get(productId)?.name ?? `#${productId}`;
  const adjustTotal = useDeliveryTotalAdjust({ plan: planAdjustTotal, apply: applyAdjustTotal, nameOf });
  const save = useDeliverySave({
    mode,
    deliveryId,
    header: {
      warehouseId: whId ?? warehouses[0]?.id ?? null,
      supplierId,
      note,
      documentType,
      linkedNoteIds,
    },
    hasLinkedNotes,
    supplierRequired: mode === "new",
    onMissingSupplier: () => supplierSelectRef.current?.focus(),
    rows: { validEntries, addedIds, rowOf, qtyBase, computeIssues, replaceAllRows },
    nameOf,
    draftSync: draft.sync,
    dirtyRef,
    focusCell,
    onSavedHeader: (values) => {
      setDate(values.date);
      setInvoiceNumber(values.documentNumber);
    },
    onDone: (saved) => navigate(LIST_PATH, saved ? { state: { openDoc: saved } } : undefined),
  });

  // ─── Глобални клавиши ───────────────────────────────────────────────────
  useWorksheetKeyboard({
    isDesktop,
    searchInputRef: searchBox.inputRef,
    searchTextRef: searchBox.searchTextRef,
    setSearch: searchBox.setSearch,
    isInGrid,
    focusSearch: searchBox.focusSearch,
    focusFirst: () => focusFirst(1),
    onPrimary: save.trySave,
    onLeave: leaveToList,
    prompt: { isOpen: () => resume.pending != null, close: resume.dismiss },
  });

  // ─── Темплейт „От минала доставка" ───────────────────────────────────────
  const applyTemplate = (id: number) => {
    const delivery = supplierDeliveries.find((d) => d.id === id);
    if (!delivery) return;
    setTemplateId(id);
    const grouped = groupDeliveryLines(delivery, decimalsOf);
    const next: typeof rows = {};
    for (const [pidStr, row] of Object.entries(grouped)) {
      const productId = +pidStr;
      // Изтрит в организацията продукт не влиза в нова доставка от темплейт
      if (isDeletedProduct(productById.get(productId))) continue;
      const recalced = { ...row, warehouseId: whId };
      calcRow(productId, recalced, "qty");
      next[productId] = recalced;
    }
    replaceAllRows(next, orderOfLines(delivery).filter((productId) => productId in next));
    setPricedSupplier(supplierId);
    touch();
  };
  const clearTemplate = () => {
    setTemplateId(null);
    replaceAllRows({}, []);
    touch();
  };

  const catalogItems = useDeliveryCatalogItems({
    products, balanceByProduct, lastPriceByProduct, addedSet, decimalsOf, rowOf, packagingById, supplierId,
  });
  const draftPrompt = resume.pending?.payload ?? null;
  const draftSummary = draftSummaryOf(draftPrompt, productById, packagingById, decimalsOf);

  const noteLocked = editedDelivery != null && docTypeOf(editedDelivery) === "note" && editedDelivery.invoiceId != null;
  const title = mode === "edit" ? `${t("inventoryPage.editDeliveryTitle")} №${deliveryId}` : t("inventoryPage.newDelivery");
  const presence = mode === "new" ? <DraftPresence lockLostBy={draft.sync.lockLostBy} /> : null;

  return (
    <InventoryTheme controlHeight={40}>
      <div className="flex h-full flex-col overflow-hidden bg-canvas text-ink">
        <DeliveryWorksheetHeader
          band={{
            back: { label: t("inventoryPage.delivery"), onClick: leaveToList },
            titleAddon: <DeliveryTypeBadge documentType={documentType} />,
            title,
            right: presence,
          }}
          mode={mode}
          documentType={documentType}
          documentTypeLocked={noteLocked}
          onDocumentTypeChange={(type) => {
            setDocumentType(type);
            if (type === "note") setLinkedNoteIds([]);
            touch();
          }}
          invoiceNumber={invoiceNumber}
          onInvoiceNumberChange={(value) => {
            setInvoiceNumber(value);
            touch();
          }}
          supplierId={supplierId}
          supplierRequired={mode === "new"}
          supplierSelectRef={supplierSelectRef}
          onSupplierChange={(id) => {
            setSupplierId(id);
            setTemplateId(null);
            setLinkedNoteIds([]);
            touch();
            // смяна на доставчика → курсорът в търсачката
            requestAnimationFrame(() => searchBox.focusSearch());
          }}
          suppliers={suppliers}
          warehouseId={whId}
          onWarehouseChange={(id) => {
            setWhId(id);
            touch();
          }}
          warehouses={warehouses}
          note={note}
          onNoteChange={(value) => {
            setNote(value);
            touch();
          }}
          templateId={templateId}
          supplierDeliveries={supplierDeliveries}
          templateLoading={supplierDeliveriesQuery.isFetching}
          productById={productById}
          decimalsOf={decimalsOf}
          onApplyTemplate={applyTemplate}
          onClearTemplate={clearTemplate}
        />
        <div
          className={`flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto ${
            isNarrow ? "px-3 py-3" : "px-6 py-4"
          }`}
        >
          {documentType === "invoice" && hasLinkedNotes ? (
            <DeliveryNotesLinkedBanner notes={linkedNotes} onChange={() => setNotesOpen(true)} />
          ) : documentType === "invoice" && supplierId != null && notesOfSupplier.length > 0 ? (
            <DeliveryNotesOfferBanner count={notesOfSupplier.length} onPick={() => setNotesOpen(true)} />
          ) : null}

          <DeliverySearchBar
            search={{
              attachInputRef: (node) => {
                searchBox.inputRef.current = node;
              },
              value: searchBox.search,
              onValueChange: searchBox.setSearch,
              onFocusChange: searchBox.setFocused,
              onInputKeyDown: searchBox.onInputKeyDown,
              dropdownOpen: searchBox.dropdownOpen,
              rows: searchRows,
              searchWords,
              supplierName,
              supplierSelected: supplierId != null,
              highlightedIndex: searchBox.highlightedIndex,
              onHighlightChange: searchBox.setHighlightedIndex,
              onPick: pickFromSearch,
              onCreateProduct: () => {
                searchBox.suppressDropdown();
                setProductModal({ initialName: searchBox.search.trim() });
              },
            }}
            onOpenCatalog={() => setCatalogOpen(true)}
            onNewProduct={() => setProductModal({ initialName: searchBox.search.trim() })}
          />

          <DeliveryProductsCard
            unitTotals={unitTotals}
            onGridKey={onGridKey}
            table={{
              addedProducts,
              rowOf,
              balanceByProduct,
              lastPriceByProduct,
              packagingById,
              warehouses,
              decimalsOf,
              cellId,
              isDesktop,
              vatApplies,
              totalNet: totalNet + notesSum.net,
              totalGross: totalGross + notesSum.gross,
              linkedNotes: invoiceNotes,
              productById,
              onAdjustTotal: adjustTotal,
              isCellInvalid: save.isCellInvalid,
              onCellChange,
              onCellBlur,
              onPackagingChange: (productId, packagingId) =>
                patchRow(productId, (row) => {
                  row.packagingId = packagingId;
                  calcRow(productId, row, "pkg");
                }),
              onExpiryChange: (productId, expiry) =>
                patchRow(productId, (row) => {
                  row.expiry = expiry;
                }),
              onRowWarehouseChange: (productId, rowWarehouseId) =>
                patchRow(productId, (row) => {
                  row.warehouseId = rowWarehouseId;
                }),
              onSideExit: focusSideways,
              onRemoveRow: removeRow,
              onFlag: setProblemFor,
            }}
          />
        </div>

        <DeliveryWorksheetFooter
          mode={mode}
          totalNet={totalNet + notesSum.net}
          totalGross={totalGross + notesSum.gross}
          vatApplies={vatApplies}
          issue={save.issues[0]?.text ?? null}
          issueIsBlocking={validEntries.length > 0}
          saving={save.pending}
          draft={draft.footer()}
          onCancel={leaveToList}
          onSave={save.trySave}
        />

        <DeliveryWorksheetModals
          mode={mode}
          documentType={documentType}
          date={date}
          invoiceNumber={invoiceNumber}
          totalGross={totalGross + notesSum.gross}
          validCount={validEntries.length}
          noteLines={notesSum.lines}
          save={save}
          nameOf={nameOf}
          decimalsOf={decimalsOf}
          rowOf={rowOf}
          patchRow={patchRow}
          problemFor={problemFor}
          onProblemClose={() => setProblemFor(null)}
          catalog={{
            open: catalogOpen,
            items: catalogItems,
            priceLabel: t("inventoryPage.wsLastPrice"),
            supplierName,
            onClose: () => setCatalogOpen(false),
            onAfterClose: (added) => {
              if (!added) searchBox.focusSearch();
            },
            onAdd: (ids, values) => {
              setCatalogOpen(false);
              addFromCatalog(ids, values);
            },
          }}
          notes={{
            open: notesOpen,
            all: pickerNotes,
            selectedIds: linkedNoteIds,
            onClose: () => setNotesOpen(false),
            onApply: (ids) => {
              setLinkedNoteIds(ids);
              setNotesOpen(false);
              touch();
            },
          }}
          productModal={productModal}
          onProductClose={() => setProductModal(null)}
          onProductCreated={(product) => {
            searchBox.setSearch("");
            createdProductRef.current = product.id;
            addProduct(product.id);
          }}
          onProductAfterClose={(created) => {
            const productId = createdProductRef.current;
            createdProductRef.current = null;
            if (created && productId != null) focusCell(productId, 1);
            else searchBox.focusSearch();
          }}
          draftPrompt={draftPrompt}
          draftSummary={draftSummary}
          onDraftContinue={resume.continueDraft}
          onDraftDiscard={resume.discardDraft}
          onDraftDismiss={resume.dismiss}
        />
      </div>
    </InventoryTheme>
  );
};

export default DeliveryWorksheetPage;
